import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, inject, HostBinding } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

@Directive({
  selector: '[mkgCopyOnClick]',
  standalone: true,
})
export class CopyOnClickDirective {
  private el = inject<ElementRef<HTMLElement>>(ElementRef);
  private _snackBar = inject(MatSnackBar);

  @HostBinding('style.user-select')
  userSelect = 'all';

  @HostBinding('style.webkitUserSelect')
  webkitUserSelect = 'all';

  @HostBinding('style.msUserSelect')
  msUserSelect = 'all';

  /** If provided, copy this value; otherwise, copy the element's textContent */
  @Input('mkgCopyOnClick') value?: string;

  /** If true, use innerText (respects breaks/visibility). Otherwise, textContent */
  @Input() copyUseInnerText = false;

  @Input() copySnack = true;
  @Input() copySnackMessage = 'Copiado!';
  @Input() copySnackEmptyMessage = 'Nada para copiar';
  @Input() copySnackDuration = 1400;
  @Input() copySnackAction?: string;

  /** Emits true/false if successfully copied */
  @Output() copied = new EventEmitter<boolean>();

  @HostListener('click', ['$event'])
  async onClick(ev: MouseEvent) {
    // avoids accidentally selecting text in some cases
    ev.preventDefault();

    const host = this.el.nativeElement;
    const text = (this.value || (this.copyUseInnerText ? host.innerText : host.textContent) || '').trim();

    if (!text) {
      this.copied.emit(false);
      return;
    }

    const ok = await this._copyText(text);
    if (ok) {
      this._emitAndSnack(true, this.copySnackMessage);
    } else {
      this._emitAndSnack(false, 'Falha ao copiar');
    }
  }

  private _emitAndSnack(ok: boolean, message: string) {
    this.copied.emit(ok);

    if (!this.copySnack || !this._snackBar) return;

    this._snackBar.open(message, this.copySnackAction, {
      duration: this.copySnackDuration,
      verticalPosition: 'bottom',
      horizontalPosition: 'center',
    });
  }

  private async _copyText(text: string): Promise<boolean> {
    // modern approach
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // falls back to fallback
    }

    // fallback (more compatible)
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '-9999px';

      document.body.appendChild(ta);
      ta.select();
      ta.setSelectionRange(0, ta.value.length);

      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}
